export const documentQueryKey = ['documents', 'indexed-list'] as const;

import type {
  DocumentDTO,
  DocumentFiltersDTO,
  DocumentNameConflictsDTO,
} from '@shared/schemas/document';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

type DocumentListResponse = {
  items: DocumentDTO[];
  meta: {
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
};

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

type ApiErrorPayload = {
  error?: {
    code?: string;
    details?: Record<string, unknown>;
    message?: string;
  };
  success?: false;
};

function createApiUrl(path: string, query?: Record<string, string | number | undefined>) {
  const url = new URL(path, apiBaseUrl);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as (T & ApiErrorPayload) | ApiErrorPayload | null;

  if (!response.ok || payload === null) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? (response.statusText || 'La requête a échoué.'),
      status: response.status,
    });
  }

  return payload as T;
}

export function getDocumentContentUrl(documentId: string) {
  return createApiUrl(`/api/documents/${documentId}/content`).toString();
}

export async function fetchRenderedAssetHtml(documentId: string, asset: 'rendered.html') {
  const response = await fetch(createApiUrl(`/api/documents/${documentId}/rendered/${asset}`), {
    credentials: 'include',
    method: 'GET',
  });

  if (!response.ok) {
    await parseJsonResponse<ApiSuccessResponse<never>>(response);
    throw new ApiError({
      message: 'Aperçu rendu indisponible.',
      status: response.status,
    });
  }

  return response.text();
}


export async function fetchXlsxDump(documentId: string): Promise<{
  sheets: Array<{
    mergedRanges: string[];
    name: string;
    rows: string[][];
  }>;
}> {
  const response = await fetch(createApiUrl(`/api/documents/${documentId}/xlsx-dump`), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<{
    sheets: Array<{
      mergedRanges: string[];
      name: string;
      rows: string[][];
    }>;
  }>(response);
  return payload;
}

export async function fetchPptxDump(documentId: string): Promise<{
  slides: Array<{
    blocks: Array<{
      isSpeakerNote: boolean;
      paragraphLevel: number;
      shapeId: string | null;
      text: string;
      type: 'paragraph' | 'list_item';
    }>;
    slide: number;
    tables: Array<{ rows: string[][]; shapeId: string | null }>;
    title: string;
  }>;
}> {
  const response = await fetch(createApiUrl(`/api/documents/${documentId}/pptx-dump`), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<{
    slides: Array<{
      blocks: Array<{
        isSpeakerNote: boolean;
        paragraphLevel: number;
        shapeId: string | null;
        text: string;
        type: 'paragraph' | 'list_item';
      }>;
      slide: number;
      tables: Array<{ rows: string[][]; shapeId: string | null }>;
      title: string;
    }>;
  }>(response);
  return payload;
}

export async function deleteIndexedDocument(documentId: string) {
  const response = await fetch(createApiUrl(`/api/documents/${documentId}`), {
    credentials: 'include',
    method: 'DELETE',
  });

  await parseJsonResponse<{ success: true }>(response);
}

export async function reindexIndexedDocument(documentId: string) {
  const response = await fetch(createApiUrl(`/api/documents/${documentId}/reindex`), {
    credentials: 'include',
    method: 'POST',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<DocumentDTO>>(response);
  return payload.data;
}

export async function listIndexedDocuments(filters: Partial<DocumentFiltersDTO> = {}) {
  const response = await fetch(createApiUrl('/api/documents', {
    page: filters.page,
    pageSize: filters.pageSize,
    origin: filters.origin,
    search: filters.search,
    status: filters.status,
  }), {
    credentials: 'include',
    method: 'GET',
  });

  return parseJsonResponse<DocumentListResponse>(response);
}

export async function checkDocumentNameConflicts(names: string[]) {
  const response = await fetch(createApiUrl('/api/documents/name-conflicts'), {
    body: JSON.stringify({ names }),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  const payload = await parseJsonResponse<ApiSuccessResponse<DocumentNameConflictsDTO>>(response);
  return payload.data.conflicts;
}

export async function uploadIndexedDocument(file: File, options?: { replace?: boolean }) {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.replace) {
    formData.append('replace', 'true');
  }

  const response = await fetch(createApiUrl('/api/documents'), {
    body: formData,
    credentials: 'include',
    method: 'POST',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<DocumentDTO> & { replacedPrevious?: boolean }>(response);
  return {
    document: payload.data,
    replacedPrevious: payload.replacedPrevious === true,
  };
}
